import { Alert, Box, Flex, HStack, Text, VStack } from '@chakra-ui/react';
import { Tooltip } from '@/components/ui/tooltip';
import { explainCopy } from '../../lib/explainCopy'
import { formatLean } from '../analyticsMap/choropleth'
import { CoverageBadge } from './CoverageBadge'
import { StatTile, vizDiv, vizSeqVar } from '../shared/viz'
import { layoutEndLabels } from './marginBandLabels'
import type { MarginBand, SimBaselineResponse, SimRunResponse } from '../../types/electionSim'

// Monte Carlo draw count — a fixed model constant (backend ElectionSimOptions.Draws default),
// surfaced here so the win-probability tooltip and model card read honestly.
const SIM_DRAWS = 1000

// The margin-band SVG maps partisan margin (pp) onto a fixed, clamped ±10 pp scale so the
// baseline and plan bands share one axis and the "even" line sits at a stable x.
const PP_MIN = -10
const PP_MAX = 10
// Layout leaves headroom above the bands for the "even" label (was clipped at y=5) and keeps
// the end labels inside the viewBox and apart from each other (layoutEndLabels in marginBandLabels.ts).
const SVG_W = 300
const SVG_H = 74
const PLOT_LEFT = 52
const PLOT_RIGHT = 292
const PLOT_W = PLOT_RIGHT - PLOT_LEFT
const EVEN_LABEL_Y = 10
const BASELINE_ROW_Y = 20
const PLAN_ROW_Y = 44

const clampPp = (pp: number) => Math.max(PP_MIN, Math.min(PP_MAX, pp))
const xOf = (pp: number) => PLOT_LEFT + ((clampPp(pp) - PP_MIN) / (PP_MAX - PP_MIN)) * PLOT_W

interface BandRowProps {
  band: MarginBand
  y: number
  label: string
  bandFill: string
  tickFill: string
  labelColor: string
  showEndLabels?: boolean
}

/** One horizontal p10–p90 band with a median tick, on the shared ±10 pp axis. */
function BandRow({ band, y, label, bandFill, tickFill, labelColor, showEndLabels }: BandRowProps) {
  const x10 = xOf(band.p10)
  const x90 = xOf(band.p90)
  const left = Math.min(x10, x90)
  const width = Math.max(2, Math.abs(x90 - x10))
  const xMed = xOf(band.p50)
  return (
    <>
      <text x={8} y={y + 8} fontSize={10} fill={labelColor}>
        {label}
      </text>
      <rect x={left} y={y} width={width} height={10} rx={4} fill={bandFill} stroke="var(--chakra-colors-border-2)" />
      <rect x={xMed - 1.5} y={y - 2} width={3} height={14} rx={1.5} fill={tickFill} />
      {showEndLabels && (
        layoutEndLabels(left, left + width, formatLean(band.p10), formatLean(band.p90),
          { plotLeft: PLOT_LEFT, plotRight: PLOT_RIGHT, svgW: SVG_W })
          .map((l) => (
            <text key={l.text} x={l.x} y={y + 24} fontSize={10} fill="var(--chakra-colors-ink-2)" textAnchor={l.anchor}>
              {l.text}
            </text>
          ))
      )}
    </>
  )
}

export interface OutcomeCardProps {
  run: SimRunResponse
  baseline: SimBaselineResponse | null | undefined
}

/**
 * Turnout/persuasion decomposition waterfall: shows how many net votes a plan moves and why,
 * broken down into aligned vs. opposing turnout (and persuasion / advanced-assumption deltas
 * when present), ending in a signed net-margin-shift total. Every number here traces back to a
 * `SimRunResponse` decomposition field — no derived point estimates.
 */
function DecompositionWaterfall({ run, primary = false }: { run: SimRunResponse; primary?: boolean }) {
  const total = run.turnoutVotersAdded ?? 0
  const aligned = run.alignedVotersAdded ?? 0
  const opposing = run.opposingVotersAdded ?? 0
  const persuaded = run.persuadedVotersAdded ?? 0
  const advanced = run.advancedVotesMoved
  const net = run.netVotesMoved ?? 0
  const r = (n: number) => Math.round(n).toLocaleString('en-US')
  return (
    <Box mt={4} bg="info.subtle" borderRadius="md" p={4} fontSize="sm">
      <Text mb={2}>Your plan turns out ~{r(total)} more voters</Text>
      <VStack align="stretch" gap={1}>
        <HStack justify="space-between"><Text>▲ {r(aligned)} aligned with you</Text><Text fontWeight="bold" color="green.600">+{r(aligned)}</Text></HStack>
        <HStack justify="space-between"><Text>▼ {r(opposing)} lean against</Text><Text fontWeight="bold" color="red.500">−{r(opposing)}</Text></HStack>
        {persuaded > 0 && (
          <HStack justify="space-between"><Text>+ {r(persuaded)} persuaded to your side</Text><Text fontWeight="bold" color="green.600">+{r(persuaded * 2)}</Text></HStack>
        )}
        {advanced != null && (
          <HStack justify="space-between"><Text>+ advanced assumptions</Text><Text fontWeight="bold" color={advanced >= 0 ? 'green.600' : 'red.500'}>{advanced >= 0 ? '+' : '−'}{r(Math.abs(advanced))}</Text></HStack>
        )}
        <Box borderTopWidth="1px" borderColor="info.fg" pt={1} mt={1}>
          <HStack justify="space-between">
            <HStack gap={1}>
              <Text fontWeight="bold">Net margin shift</Text>
              <Tooltip showArrow content={primary ? explainCopy.primaryNetMovement() : (net < 0 ? explainCopy.netMovementNegative() : explainCopy.netMovementPositive())} positioning={{
                placement: "top"
              }}>
                <Box as="span" aria-label="Explain net margin shift" tabIndex={0} cursor="help" fontSize="xs" color="info.fg" borderWidth="1px" borderColor="info.fg" borderRadius="full" px={1.5}>i</Box>
              </Tooltip>
            </HStack>
            <Text fontWeight="bold" color={net >= 0 ? 'green.600' : 'red.500'}>{net >= 0 ? '+' : '−'}{r(Math.abs(net))}</Text>
          </HStack>
        </Box>
      </VStack>
      {net < 0 && !primary && <Text mt={2} fontSize="xs" color="gray.600">{explainCopy.netMovementNegative()}</Text>}
    </Box>
  );
}

/**
 * Single-race outcome pane (spec §slice-4): win-probability with a delta arrow, the dual
 * margin-band chart (baseline gray + plan blue), the votes-needed callout, and the model-card
 * footer. Every quantitative claim is a band or a probability — no point estimates — and each
 * tooltip is wired to `explainCopy` (transparency contract).
 */
export function OutcomeCard({ run, baseline }: OutcomeCardProps) {
  const { coverage } = run

  // Refusal short-circuits the whole outcome: we never render odds for a committee that opted out.
  if (coverage.refusalReason) {
    return (
      <Box bg="surface" p={6} borderRadius="lg" shadow="md">
        <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" mb={2}>
          Outcome
        </Text>
        <Alert.Root status="info" borderRadius="md">
          <Alert.Indicator />
          {coverage.refusalReason}
        </Alert.Root>
      </Box>
    );
  }

  if (coverage.basis === 'primary') {
    return (
      <Box bg="surface" p={6} borderRadius="lg" shadow="md">
        <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" mb={3}>Outcome</Text>
        <Alert.Root status="info" borderRadius="md" mb={4} fontSize="sm">
          <Alert.Indicator />
          Primary mode — targeting only, no outcome forecast. {explainCopy.primaryMode()}
        </Alert.Root>
        <DecompositionWaterfall run={run} primary />
        <Text fontSize="xs" color="gray.600" mt={2}>{explainCopy.targetingBasisNote()}</Text>
      </Box>
    );
  }

  if (coverage.basis === 'topOfTicket') {
    return (
      <Box bg="surface" p={6} borderRadius="lg" shadow="md">
        <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" mb={3}>Outcome</Text>
        <Alert.Root status="warning" borderRadius="md" mb={4} fontSize="sm">
          <Alert.Indicator />
          {explainCopy.topOfTicketBasis()}
        </Alert.Root>
        <HStack gap={2} mb={2} wrap="wrap">
          <CoverageBadge coverage="TopOfTicket" />
          <Text fontSize="xs" color="gray.600">
            {coverage.modeledFromTopOfTicket} precincts · baseline{' '}
            {(run.baselineContestsUsed ?? []).join(' · ') || 'presidential'}
          </Text>
        </HStack>
        <DecompositionWaterfall run={run} />
        <Text fontSize="xs" color="gray.600" mt={2}>{explainCopy.targetingBasisNote()}</Text>
      </Box>
    );
  }

  const staleRefs = run.staleRefs ?? []
  const contestsUsed = run.baselineContestsUsed ?? []
  const unitCount = coverage.modeledFromReturns + coverage.modeledFromTopOfTicket + coverage.insufficient

  const winPct = run.winProbability != null ? Math.round(run.winProbability * 100) : null
  const basePct = run.baselineWinProbability != null ? Math.round(run.baselineWinProbability * 100) : null
  const improved = winPct != null && basePct != null && winPct >= basePct
  const leanFormatted = run.marginPp ? formatLean(run.marginPp.p50) : null
  const votesNeededSentence =
    run.votesNeeded != null && run.netVotesMoved != null
      ? explainCopy.votesNeeded(run.votesNeeded, run.netVotesMoved, run.baselineMarginVotes?.p50 ?? null)
      : null
  // One coverage verdict for the model card: this branch only renders for a returns-modeled run,
  // so the verdict is Modeled whenever any precinct was modeled; precincts without usable results
  // are reported as detail (they are excluded from the forecast, not a competing verdict).
  const coverageVerdict = coverage.modeledFromReturns > 0 ? 'Modeled' : 'Insufficient'

  return (
    <Box bg="surface" p={6} borderRadius="lg" shadow="md">
      <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" mb={3}>
        Outcome
      </Text>

      {staleRefs.length > 0 && (
        <Alert.Root status="warning" borderRadius="md" mb={4} fontSize="sm">
          <Alert.Indicator />
          Scenario references a deleted list/turf — re-target before running.
        </Alert.Root>
      )}

      <Flex gap={8} align="center" wrap="wrap">
        <StatTile
          label="Win probability with this plan"
          size="lg"
          value={
            <Tooltip
              showArrow
              content={explainCopy.winProbability(SIM_DRAWS, winPct ?? 0, leanFormatted)}
              positioning={{
                placement: "top"
              }}
            >
              <Text as="span" cursor="help">
                {winPct != null ? `${winPct}%` : '—'}
              </Text>
            </Tooltip>
          }
          delta={basePct != null ? { dir: improved ? 'up' : 'down', value: `from ${basePct}%`, tone: improved ? 'success' : 'danger' } : undefined}
        />

        {(baseline?.marginPp || run.marginPp) && (
          <Box w="full" maxW={`${SVG_W}px`} data-testid="margin-band-chart">
            <Tooltip
              showArrow
              content={
                run.marginPp
                  ? explainCopy.marginBand(run.marginPp.p10, run.marginPp.p90)
                  : 'Margin band'
              }
              positioning={{
                placement: "top"
              }}
            >
              <svg
                viewBox={`0 0 ${SVG_W} ${SVG_H}`}
                width="100%"
                role="img"
                aria-label="Margin bands: baseline vs. with plan, on a ±10 pp scale"
                style={{ cursor: 'help', display: 'block', maxWidth: SVG_W, overflow: 'visible' }}
              >
                {/* "even" reference line at 0 pp */}
                <line
                  x1={xOf(0)}
                  y1={EVEN_LABEL_Y + 3}
                  x2={xOf(0)}
                  y2={PLAN_ROW_Y + 14}
                  stroke="var(--chakra-colors-muted)"
                  strokeWidth={1}
                  strokeDasharray="3 3"
                />
                <text x={xOf(0)} y={EVEN_LABEL_Y} fontSize={9} fill="var(--chakra-colors-muted)" textAnchor="middle">
                  even
                </text>
                {baseline?.marginPp && (
                  <BandRow
                    band={baseline.marginPp}
                    y={BASELINE_ROW_Y}
                    label="baseline"
                    bandFill="var(--chakra-colors-border)"
                    tickFill="var(--chakra-colors-muted)"
                    labelColor="var(--chakra-colors-muted)"
                  />
                )}
                {run.marginPp && (
                  <BandRow
                    band={run.marginPp}
                    y={PLAN_ROW_Y}
                    label="with plan"
                    bandFill={vizDiv.pos}
                    tickFill={vizSeqVar(700)}
                    labelColor="var(--chakra-colors-ink-2)"
                    showEndLabels
                  />
                )}
              </svg>
            </Tooltip>
          </Box>
        )}
      </Flex>

      <DecompositionWaterfall run={run} />

      {votesNeededSentence != null && (
        <Box mt={3} fontSize="sm" color="gray.700">
          <Tooltip showArrow content={votesNeededSentence} positioning={{
            placement: "top"
          }}>
            <Text as="span" tabIndex={0} aria-label="Explain votes needed vs. moved" cursor="help">
              {votesNeededSentence}
            </Text>
          </Tooltip>
        </Box>
      )}

      <Box mt={5} bg="gray.50" borderRadius="md" p={4}>
        <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" mb={2}>
          Model card — what this run used
        </Text>
        <HStack gap={2} mb={2} wrap="wrap">
          <CoverageBadge coverage={coverageVerdict} />
          <Text fontSize="xs" color="gray.600">
            {explainCopy.modelCoverage(coverage.modeledFromReturns, unitCount)}
          </Text>
        </HStack>
        <Text fontSize="xs" color="gray.600">
          Baseline from {contestsUsed.length > 0 ? contestsUsed.join(' · ') : 'no prior contests'};
          uncontested races excluded · {SIM_DRAWS.toLocaleString('en-US')} draws with correlated swing.
        </Text>
        <Text fontSize="xs" color="gray.700" fontWeight="semibold" mt={1}>
          Every number above is a band or a probability — no point estimates.
        </Text>
      </Box>
    </Box>
  );
}
